import { Link } from "react-router";
import { TeamFlag } from "@/components/team-flag";
import {
  TASK_ICON,
  dateFromKey,
  formatSlotTime,
  localDateKey,
  localMinutesOfDay,
  nowInZone,
  scheduleStatusMeta,
  taskTypeShort,
} from "@/domain/schedule";
import type { ScheduleSlot, ScheduleTeamEntry } from "@/api/hooks/use-schedule";

interface DaySlot {
  slot: ScheduleSlot;
  team: ScheduleTeamEntry;
  minutes: number; // time-of-day, parsed once for sorting
}

function dayHeader(key: string): { wd: string; md: string } {
  const dt = dateFromKey(key);
  return {
    wd: dt.toLocaleDateString("en-US", { weekday: "short" }),
    md: dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  };
}

function SlotEntry({
  slot,
  team,
  canManage,
  canViewBuilds,
  eventId,
  onEdit,
}: {
  slot: ScheduleSlot;
  team: ScheduleTeamEntry;
  canManage: boolean;
  canViewBuilds: boolean;
  eventId: string;
  onEdit: (slot: ScheduleSlot) => void;
}) {
  const m = scheduleStatusMeta(slot.status);
  const Icon = TASK_ICON[slot.task_type];
  const interactive = canManage || canViewBuilds;
  const className = `block w-full rounded-r border border-border border-l-3 bg-card ${m.border} px-2 py-1.5 text-left ${
    interactive ? "cursor-pointer hover:bg-muted/50" : ""
  }`;
  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold tabular-nums">{formatSlotTime(slot.scheduled_start)}</span>
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${m.dot}`} />
      </div>
      <div className="mt-0.5 flex items-center gap-1 text-xs">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium">{taskTypeShort(slot.task_type)}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <TeamFlag iso_alpha2={team.team_iso_alpha2} size="xs" />
        <span className="font-medium">{team.team_code}</span>
      </div>
    </>
  );

  if (canManage) {
    return (
      <button type="button" onClick={() => onEdit(slot)} className={className} title={m.label}>
        {content}
      </button>
    );
  }
  if (canViewBuilds) {
    return (
      <Link
        to={`/events/${eventId}/builds/${slot.event_build_id}`}
        className={className}
        title={m.label}
      >
        {content}
      </Link>
    );
  }
  return (
    <div className={className} title={m.label}>
      {content}
    </div>
  );
}

export function ScheduleDays({
  teams,
  canManage,
  canViewBuilds,
  eventId,
  timeZone,
  onEdit,
}: {
  teams: ScheduleTeamEntry[];
  canManage: boolean;
  canViewBuilds: boolean;
  eventId: string;
  timeZone?: string;
  onEdit: (slot: ScheduleSlot) => void;
}) {
  // Group scheduled slots (across teams) by day, each list sorted by time.
  const byDay = new Map<string, DaySlot[]>();
  for (const team of teams) {
    for (const slot of team.slots) {
      if (!slot.scheduled_start) continue;
      const day = localDateKey(slot.scheduled_start);
      let list = byDay.get(day);
      if (!list) {
        list = [];
        byDay.set(day, list);
      }
      list.push({ slot, team, minutes: localMinutesOfDay(slot.scheduled_start) });
    }
  }
  for (const list of byDay.values()) {
    list.sort((a, b) => a.minutes - b.minutes);
  }
  const days = [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b));

  if (days.length === 0) {
    return <div className="text-sm text-muted-foreground">No scheduled work yet.</div>;
  }

  const today = nowInZone(timeZone).dateKey;

  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex gap-3">
        {days.map(([day, list]) => {
          const { wd, md } = dayHeader(day);
          return (
            <div key={day} className="w-52 shrink-0 rounded-lg border border-border bg-card">
              <div
                className={`rounded-t-lg border-b border-border px-3 py-2 ${
                  day === today ? "bg-primary/10" : "bg-muted/30"
                }`}
              >
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{wd}</div>
                <div className="text-sm font-semibold">{md}</div>
                <div className="text-[10px] text-muted-foreground">
                  {list.length} slot{list.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="space-y-1.5 p-2">
                {list.map(({ slot, team }) => (
                  <SlotEntry
                    key={slot.id}
                    slot={slot}
                    team={team}
                    canManage={canManage}
                    canViewBuilds={canViewBuilds}
                    eventId={eventId}
                    onEdit={onEdit}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
